import type { MeResponse, MembershipRole } from "@safeplate/contracts";
import { Banner, cn } from "@safeplate/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import {
  Bell,
  BellRing,
  Building2,
  ClipboardPlus,
  FileClock,
  FileText,
  History,
  LayoutDashboard,
  ListChecks,
  MapPin,
  Menu,
  Plus,
  Settings,
  Settings2,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import { getLocations, setActiveOrganisationHeader } from "../api/client.js";
import { Brand } from "../components/brand.js";
import { SelectControl } from "../components/select-control.js";
import { getLocationDashboard } from "../dashboard/dashboard-api.js";
import {
  INBOX_POLL_MS,
  inboxQueryKey,
  listInbox,
  unreadCount,
} from "../inbox/inbox-api.js";
import {
  useComputerActions,
  type ComputerActions,
} from "../quick-access/computer-actions.js";
import { useIdleLock } from "../quick-access/idle-lock.js";
import {
  idleLockDelayMs,
  initialsOf,
  roleLabels,
} from "../quick-access/people-on-computer.js";
import { usePeopleOnComputer } from "../quick-access/people-store.js";
import {
  organisationChoices,
  readStoredSelection,
  resolveSelection,
  selectionKeys,
  storeSelection,
} from "../state/selection.js";
import {
  canManage,
  countLabel,
  moreItems,
  newLogItem,
  phoneBar,
  sidebarGroups,
  userMenu,
  type NavIcon,
  type NavItem,
} from "./navigation.js";
import { RoleGate } from "./role-gate.js";
import { ShellContext } from "./shell-context.js";

const icons: Record<NavIcon, LucideIcon> = {
  attention: BellRing,
  history: History,
  log: ClipboardPlus,
  more: Menu,
  records: FileClock,
  reports: FileText,
  settings: Settings,
  setup: Settings2,
  tasks: ListChecks,
  team: Users,
  today: LayoutDashboard,
};

// Polling is slow on purpose: the API sleeps when idle and hidden tabs do not poll.
const COUNT_POLL_MS = INBOX_POLL_MS;

function CountBadge({ count }: { count: number }) {
  const label = countLabel(count);
  if (!label) return null;
  return (
    <span className="ml-auto grid min-w-5 place-items-center rounded-full bg-amber-100 px-1.5 text-[0.65rem] leading-5 font-bold text-amber-900">
      {label}
    </span>
  );
}

function SidebarLink({
  attentionCount,
  item,
}: {
  attentionCount: number;
  item: NavItem;
}) {
  const Icon = icons[item.icon];
  return (
    <Link
      activeOptions={{ exact: item.to === "/" }}
      activeProps={{ className: "bg-emerald-800 text-white shadow-sm" }}
      className="group flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-semibold text-stone-600 transition hover:bg-stone-100 hover:text-stone-950"
      to={item.to}
    >
      <Icon
        aria-hidden="true"
        className="size-[1.05rem] shrink-0"
        strokeWidth={2.1}
      />
      {item.label}
      {item.badge === "attention" ? (
        <CountBadge count={attentionCount} />
      ) : null}
    </Link>
  );
}

function UserMenu({
  actions,
  name,
  placement,
  role,
  variant,
}: {
  actions: ComputerActions;
  name: string;
  placement: "above" | "below";
  role: MembershipRole;
  variant: "avatar" | "sidebar";
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const menu = userMenu(role, actions.peopleCount);

  function items(): HTMLElement[] {
    return Array.from(
      list.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not(:disabled)',
      ) ?? [],
    );
  }

  useEffect(() => {
    if (open) items()[0]?.focus();
  }, [open]);

  function onMenuKey(event: ReactKeyboardEvent) {
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    const entries = items();
    const index = entries.indexOf(document.activeElement as HTMLElement);
    let next: number | null = null;
    if (event.key === "ArrowDown") next = (index + 1) % entries.length;
    else if (event.key === "ArrowUp")
      next = (index - 1 + entries.length) % entries.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = entries.length - 1;
    if (next === null) return;
    event.preventDefault();
    entries[next]?.focus();
  }

  useEffect(() => {
    if (!open) return undefined;
    function onPointer(event: PointerEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const itemClass =
    "flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm font-semibold text-stone-800 hover:bg-stone-100 disabled:opacity-50";

  function choose(action: () => Promise<void>) {
    setOpen(false);
    void action();
  }

  return (
    <div className="relative" ref={root}>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={variant === "avatar" ? name : undefined}
        className={cn(
          "flex items-center gap-3 rounded-xl text-left outline-none",
          variant === "sidebar" ? "w-full px-2 py-1.5 hover:bg-stone-100" : "",
        )}
        onClick={() => setOpen((current) => !current)}
        ref={trigger}
        type="button"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-emerald-100 text-xs font-black text-emerald-900">
          {initialsOf(name)}
        </span>
        {variant === "sidebar" ? (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-bold">{name}</span>
            <span className="block truncate text-xs text-stone-500">
              {roleLabels[role]}
            </span>
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          className={cn(
            "absolute z-50 w-64 rounded-xl border border-stone-200 bg-surface p-1.5 shadow-lg",
            placement === "above"
              ? "bottom-full left-0 mb-2"
              : "top-full right-0 mt-2",
          )}
          onKeyDown={onMenuKey}
          ref={list}
          role="menu"
        >
          <p className="truncate px-3 py-2 text-xs font-semibold text-stone-500">
            {name} · {roleLabels[role]}
          </p>
          <Link
            className={itemClass}
            onClick={() => setOpen(false)}
            role="menuitem"
            to="/settings"
          >
            Settings
          </Link>
          {menu.switchPerson ? (
            <button
              className={itemClass}
              disabled={actions.pending}
              onClick={() => choose(actions.switchPerson)}
              role="menuitem"
              type="button"
            >
              Switch person
            </button>
          ) : null}
          {menu.addPerson ? (
            <button
              className={itemClass}
              disabled={actions.pending}
              onClick={() => choose(actions.addPerson)}
              role="menuitem"
              type="button"
            >
              Add another person
            </button>
          ) : null}
          <button
            className={itemClass}
            disabled={actions.pending}
            onClick={() => choose(actions.signOutOfComputer)}
            role="menuitem"
            type="button"
          >
            {menu.signOutLabel}
          </button>
          {actions.error ? (
            <p
              className="px-3 py-2 text-xs font-semibold text-red-800"
              role="alert"
            >
              {actions.error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function BellLink({ unread }: { unread: number }) {
  const label = countLabel(unread);
  return (
    <Link
      aria-label={label ? `Inbox, ${label} unread` : "Inbox"}
      className="relative grid size-10 place-items-center rounded-xl text-stone-600 hover:bg-stone-100 hover:text-stone-950"
      to="/inbox"
    >
      <Bell aria-hidden="true" className="size-5" />
      {label ? (
        <span className="absolute top-1 right-1 grid min-w-4 place-items-center rounded-full bg-emerald-800 px-1 text-[0.6rem] leading-4 font-bold text-white">
          {label}
        </span>
      ) : null}
    </Link>
  );
}

const entitlementBanners: Record<
  string,
  { title: string; tone: "attention" | "danger" }
> = {
  grace: { title: "Billing grace period", tone: "attention" },
  none: { title: "This location isn’t licensed", tone: "attention" },
  past_due: { title: "Payment overdue", tone: "attention" },
  cancelled: { title: "This location is read-only", tone: "danger" },
  read_only: { title: "This location is read-only", tone: "danger" },
};

export function AppShell({ me }: { me: MeResponse }) {
  const organisations = useMemo(() => organisationChoices(me), [me]);
  const [activeOrganisationId, setActiveOrganisationId] = useState(
    () =>
      resolveSelection(
        organisations,
        readStoredSelection(selectionKeys.organisation),
      ) ?? organisations[0]!.id,
  );
  const [activeLocationId, setActiveLocationId] = useState<string | null>(() =>
    readStoredSelection(selectionKeys.location),
  );
  const [moreOpen, setMoreOpen] = useState(false);
  // Printable pages, such as QR labels, show without the navigation.
  const bare = useRouterState({
    select: (state) => state.location.pathname.startsWith("/labels/"),
  });
  const moreRegion = useRef<HTMLDivElement>(null);
  const moreButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!moreOpen) return undefined;
    moreRegion.current
      ?.querySelector<HTMLElement>(
        "#more-navigation a, #more-navigation button",
      )
      ?.focus();
    function onPointer(event: PointerEvent) {
      if (!moreRegion.current?.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMoreOpen(false);
        moreButton.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  useEffect(() => {
    setActiveOrganisationHeader(activeOrganisationId);
    storeSelection(selectionKeys.organisation, activeOrganisationId);
  }, [activeOrganisationId]);

  const locationsQuery = useQuery({
    enabled: Boolean(activeOrganisationId),
    queryFn: () => getLocations(activeOrganisationId),
    queryKey: ["locations", activeOrganisationId],
  });

  const locations = locationsQuery.data ?? [];
  const resolvedLocationId = resolveSelection(locations, activeLocationId);
  const activeLocation =
    locations.find((location) => location.id === resolvedLocationId) ?? null;

  useEffect(() => {
    if (resolvedLocationId !== activeLocationId) {
      setActiveLocationId(resolvedLocationId);
    }
    storeSelection(selectionKeys.location, resolvedLocationId);
  }, [activeLocationId, resolvedLocationId]);

  const activeOrganisation =
    organisations.find(
      (organisation) => organisation.id === activeOrganisationId,
    ) ?? organisations[0]!;
  const role = activeOrganisation.role;
  const manager = canManage(role);

  function changeOrganisation(orgId: string) {
    setActiveOrganisationHeader(orgId);
    setActiveOrganisationId(orgId);
    setActiveLocationId(null);
    storeSelection(selectionKeys.location, null);
  }

  const attention = useQuery({
    enabled: manager && Boolean(activeLocation),
    queryFn: () => getLocationDashboard(activeLocation!.id),
    queryKey: ["dashboard", activeLocation?.id],
    refetchInterval: COUNT_POLL_MS,
    select: (metrics) => metrics.attention.total,
  });
  const inbox = useQuery({
    queryFn: () => listInbox(activeOrganisationId),
    queryKey: inboxQueryKey(activeOrganisationId),
    refetchInterval: INBOX_POLL_MS,
    select: (page) => unreadCount(page.items),
  });
  const attentionCount = attention.data ?? 0;
  const unread = inbox.data ?? 0;

  const people = usePeopleOnComputer();
  const ownStaffProfileIds = useMemo(
    () =>
      me.staff_profiles
        .filter((profile) => profile.org_id === activeOrganisationId)
        .map((profile) => profile.staff_profile_id),
    [me, activeOrganisationId],
  );
  const actions = useComputerActions({
    authMethod: me.session.auth_method,
    staffProfileIds: ownStaffProfileIds,
    userId: me.user.id,
  });
  useIdleLock(idleLockDelayMs(people.tiles.length), () => {
    void actions.lockForIdle();
  });

  const shellContext = {
    activeLocation,
    activeOrganisationId,
    activeRole: role,
    authMethod: me.session.auth_method,
    locationError: locationsQuery.error,
    locations,
    locationsPending: locationsQuery.isPending,
    ownStaffProfileIds,
    refreshLocations: locationsQuery.refetch,
    selectLocation: setActiveLocationId,
    userId: me.user.id,
    userName: me.user.name,
  };

  const banner = activeLocation
    ? entitlementBanners[activeLocation.entitlement_state]
    : undefined;
  const bar = phoneBar(role);
  const more = moreItems(role);

  if (bare)
    return (
      <ShellContext.Provider value={shellContext}>
        <Outlet />
      </ShellContext.Provider>
    );

  return (
    <ShellContext.Provider value={shellContext}>
      <div className="min-h-dvh bg-canvas text-stone-950">
        <a
          className="sr-only z-50 rounded-md bg-surface px-4 py-3 text-sm font-semibold text-emerald-900 focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
          href="#main"
        >
          Skip to content
        </a>
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-stone-200 bg-surface px-4 py-5 lg:flex">
          <div className="px-2">
            <Brand />
          </div>
          {manager ? (
            <Link
              className="mt-6 flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-800 text-sm font-semibold text-white shadow-sm hover:bg-emerald-900"
              to={newLogItem.to}
            >
              <Plus aria-hidden="true" className="size-4" />
              {newLogItem.label}
            </Link>
          ) : null}
          <nav
            aria-label="Primary navigation"
            className="mt-7 flex-1 space-y-7 overflow-y-auto"
          >
            {sidebarGroups(role).map((group) => (
              <div key={group.label ?? "main"}>
                {group.label ? (
                  <p className="mb-2 px-3 text-[0.65rem] font-bold tracking-[0.14em] text-stone-500 uppercase">
                    {group.label}
                  </p>
                ) : null}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <SidebarLink
                      attentionCount={attentionCount}
                      item={item}
                      key={item.to + item.label}
                    />
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="border-t border-stone-200 pt-4">
            <UserMenu
              actions={actions}
              name={me.user.name}
              placement="above"
              role={role}
              variant="sidebar"
            />
          </div>
        </aside>

        <div className="lg:pl-64">
          <header className="sticky top-0 z-20 border-b border-stone-200/90 bg-canvas/92 px-4 py-3 backdrop-blur-xl sm:px-6 lg:px-9">
            <div className="mx-auto flex max-w-[1440px] items-center gap-3">
              <div className="mr-auto lg:hidden">
                <Brand compact />
              </div>
              <div className="hidden min-w-0 flex-1 lg:block">
                <p className="truncate text-xs font-bold text-stone-500">
                  {activeOrganisation.name}
                </p>
              </div>
              {organisations.length > 1 ? (
                <SelectControl
                  ariaLabel="Business"
                  icon={
                    <Building2
                      aria-hidden="true"
                      className="size-4 shrink-0 text-stone-500"
                    />
                  }
                  onChange={changeOrganisation}
                  options={organisations.map((organisation) => ({
                    label: organisation.name,
                    value: organisation.id,
                  }))}
                  value={activeOrganisationId}
                />
              ) : null}
              {locations.length > 1 || locationsQuery.isPending ? (
                <SelectControl
                  ariaLabel="Location"
                  disabled={locationsQuery.isPending || locations.length === 0}
                  icon={
                    <MapPin
                      aria-hidden="true"
                      className="size-4 shrink-0 text-stone-500"
                    />
                  }
                  onChange={(locationId) =>
                    setActiveLocationId(locationId || null)
                  }
                  options={
                    locations.length > 0
                      ? locations.map((location) => ({
                          label: location.name,
                          value: location.id,
                        }))
                      : [{ label: "Loading…", value: "" }]
                  }
                  value={resolvedLocationId ?? ""}
                />
              ) : activeLocation &&
                activeLocation.name !== activeOrganisation.name ? (
                <p className="truncate text-sm font-semibold">
                  {activeLocation.name}
                </p>
              ) : null}
              <BellLink unread={unread} />
              <div className="lg:hidden">
                <UserMenu
                  actions={actions}
                  name={me.user.name}
                  placement="below"
                  role={role}
                  variant="avatar"
                />
              </div>
            </div>
          </header>

          <main
            className="mx-auto max-w-[1440px] px-4 py-6 pb-28 sm:px-6 lg:px-9 lg:py-8 lg:pb-8"
            id="main"
            tabIndex={-1}
          >
            {banner ? (
              <Banner
                action={
                  role === "admin" ? (
                    <Link
                      className="text-sm font-bold underline underline-offset-2"
                      to="/settings/plan"
                    >
                      Plan & billing
                    </Link>
                  ) : undefined
                }
                className="mb-5"
                title={banner.title}
                tone={banner.tone}
              />
            ) : null}
            <RoleGate role={role}>
              <Outlet />
            </RoleGate>
          </main>
        </div>

        <div ref={moreRegion}>
          {moreOpen && more.length > 0 ? (
            <nav
              aria-label="More"
              className="fixed inset-x-3 bottom-22 z-40 max-h-[70dvh] overflow-y-auto rounded-xl border border-stone-200 bg-surface p-2 shadow-lg lg:hidden"
              id="more-navigation"
            >
              {more.map((item) => {
                const Icon = icons[item.icon];
                return (
                  <Link
                    activeProps={{ className: "bg-emerald-800 text-white" }}
                    className="flex min-h-12 items-center gap-3 rounded-xl px-3 text-sm font-semibold text-stone-700"
                    key={item.to}
                    onClick={() => setMoreOpen(false)}
                    to={item.to}
                  >
                    <Icon aria-hidden="true" className="size-[1.05rem]" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          ) : null}
          <nav
            aria-label="Mobile navigation"
            className="fixed inset-x-3 bottom-3 z-40 grid rounded-2xl border border-stone-200 bg-surface/95 p-1.5 shadow-lg backdrop-blur-xl lg:hidden"
            style={{
              gridTemplateColumns: `repeat(${bar.length}, minmax(0, 1fr))`,
            }}
          >
            {bar.map((item) => {
              const Icon = icons[item.icon];
              if (item.kind === "more") {
                return (
                  <button
                    aria-controls={moreOpen ? "more-navigation" : undefined}
                    aria-expanded={moreOpen}
                    ref={moreButton}
                    className={cn(
                      "flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[0.65rem] font-bold text-stone-600",
                      moreOpen && "bg-emerald-800 text-white",
                    )}
                    key="more"
                    onClick={() => setMoreOpen((open) => !open)}
                    type="button"
                  >
                    <Icon aria-hidden="true" className="size-4" />
                    {item.label}
                  </button>
                );
              }
              if (item.kind === "log") {
                return (
                  <Link
                    className="flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl bg-emerald-800 text-[0.65rem] font-bold text-white"
                    key="log"
                    onClick={() => setMoreOpen(false)}
                    to={item.to!}
                  >
                    <Plus aria-hidden="true" className="size-4" />
                    {item.label}
                  </Link>
                );
              }
              return (
                <Link
                  activeOptions={{ exact: item.to === "/" }}
                  activeProps={{ className: "bg-emerald-800 text-white" }}
                  className="relative flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[0.65rem] font-bold text-stone-600"
                  key={item.label}
                  onClick={() => setMoreOpen(false)}
                  to={item.to!}
                >
                  <Icon aria-hidden="true" className="size-4" />
                  {item.label}
                  {item.badge === "attention" && countLabel(attentionCount) ? (
                    <span className="absolute top-1 right-[22%] grid min-w-4 place-items-center rounded-full bg-amber-100 px-1 text-[0.6rem] leading-4 font-bold text-amber-900">
                      {countLabel(attentionCount)}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </ShellContext.Provider>
  );
}
